<template>
  <aside
    class="activity-promotion"
    role="region"
    :aria-label="$t('activityBanner.ariaLabel')"
  >
    <div class="activity-promotion__content">
      <span class="activity-promotion__badge" aria-hidden="true">
        <i class="el-icon-time"></i>
      </span>
      <span v-if="hasCountdown" class="activity-promotion__countdown">
        <span>{{ $t("activityBanner.remaining") }}</span>
        <strong>{{ countdown.days }}</strong>
        <span>{{ $t("activityBanner.days") }}</span>
        <strong>{{ countdown.hours }}</strong>
        <span>{{ $t("activityBanner.hours") }}</span>
        <strong>{{ countdown.minutes }}</strong>
        <span>{{ $t("activityBanner.minutes") }}</span>
        <strong>{{ countdown.seconds }}</strong>
        <span>{{ $t("activityBanner.seconds") }}</span>
      </span>
      <span v-else-if="isLongTerm" class="activity-promotion__countdown activity-promotion__long-term">
        {{ $t("activityBanner.longTerm") }}
      </span>
      <span v-if="activityName" class="activity-promotion__name">
        {{ activityName }}
      </span>
      <strong class="activity-promotion__title">
        {{ activityMainTitle }}
      </strong>
      <span class="activity-promotion__divider" aria-hidden="true"></span>
      <span class="activity-promotion__description">
        {{ activitySubTitle }}
      </span>
      <button
        type="button"
        class="activity-promotion__action"
        @click="$emit('action')"
      >
        {{ $t("activityBanner.action") }}
        <i class="el-icon-arrow-right" aria-hidden="true"></i>
      </button>
    </div>
    <button
      type="button"
      class="activity-promotion__close"
      :aria-label="$t('activityBanner.close')"
      :title="$t('activityBanner.close')"
      @click="$emit('close')"
    >
      <i class="el-icon-close" aria-hidden="true"></i>
    </button>
  </aside>
</template>

<script>
export default {
  name: "ActivityPromotionBanner",
  props: {
    activity: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      remainingMilliseconds: 0,
      countdownTimer: null,
    };
  },
  computed: {
    activityEndDate() {
      return this.activity.endDate || "";
    },
    activityMainTitle() {
      return this.activity.mainTitle || this.$t("activityBanner.title");
    },
    activitySubTitle() {
      return this.activity.subTitle || this.$t("activityBanner.description");
    },
    activityName() {
      return this.activity.activityName || "";
    },
    countdown() {
      const totalSeconds = Math.max(
        0,
        Math.floor(this.remainingMilliseconds / 1000),
      );
      return {
        days: Math.floor(totalSeconds / 86400),
        hours: String(Math.floor((totalSeconds % 86400) / 3600)).padStart(
          2,
          "0",
        ),
        minutes: String(Math.floor((totalSeconds % 3600) / 60)).padStart(
          2,
          "0",
        ),
        seconds: String(totalSeconds % 60).padStart(2, "0"),
      };
    },
    hasCountdown() {
      return (
        this.remainingMilliseconds > 0 &&
        !/^9999(?:-|$)/.test(String(this.activityEndDate || "").trim())
      );
    },
    isLongTerm() {
      return /^9999(?:-|$)/.test(String(this.activityEndDate || "").trim());
    },
  },
  watch: {
    activityEndDate: {
      immediate: true,
      handler() {
        this.updateCountdown();
      },
    },
  },
  mounted() {
    this.countdownTimer = window.setInterval(this.updateCountdown, 1000);
  },
  beforeDestroy() {
    window.clearInterval(this.countdownTimer);
  },
  methods: {
    getActivityEndTimestamp() {
      const value = String(this.activityEndDate || "").trim();
      if (!value) return 0;
      const normalized = /^\d{4}-\d{2}-\d{2}$/.test(value)
        ? `${value}T23:59:59+08:00`
        : value;
      const timestamp = new Date(normalized).getTime();
      return Number.isNaN(timestamp) ? 0 : timestamp;
    },
    updateCountdown() {
      const endTimestamp = this.getActivityEndTimestamp();
      this.remainingMilliseconds = endTimestamp
        ? Math.max(0, endTimestamp - Date.now())
        : 0;
    },
  },
};
</script>

<style lang="less" scoped>
.activity-promotion {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1400;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 48PX;
  padding: 0 56PX 0 24PX;
  overflow: hidden;
  color: #fff;
  background:
    linear-gradient(102deg, rgba(25, 255, 202, 0.48) 0%, transparent 29%),
    linear-gradient(76deg, transparent 21%, rgba(91, 137, 255, 0.58) 48%, transparent 78%),
    linear-gradient(112deg, transparent 30%, rgba(235, 92, 255, 0.54) 61%, transparent 90%),
    linear-gradient(90deg, #162746 0%, #302268 45%, #3a1c4f 100%);
  border-bottom: 1PX solid rgba(207, 181, 255, 0.5);
  box-shadow:
    inset 0 -1PX 0 rgba(110, 255, 224, 0.36),
    inset 0 1PX 0 rgba(224, 120, 255, 0.3),
    0 6PX 22PX rgba(8, 5, 18, 0.42),
    0 0 30PX rgba(117, 82, 255, 0.38);
}

.activity-promotion::before {
  content: "";
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: linear-gradient(
    115deg,
    transparent 18%,
    rgba(255, 255, 255, 0.08) 44%,
    transparent 68%
  );
}

.activity-promotion__content {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  gap: 12PX;
}

.activity-promotion__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 28PX;
  height: 28PX;
  border-radius: 50%;
  color: #2b1948;
  background: #ffd166;
  box-shadow: 0 0 16PX rgba(255, 209, 102, 0.34);
}

.activity-promotion__countdown {
  display: inline-flex;
  align-items: baseline;
  flex: 0 0 auto;
  gap: 3PX;
  padding: 5PX 9PX;
  border-radius: 999PX;
  color: rgba(255, 255, 255, 0.88);
  background: rgba(255, 255, 255, 0.1);
  font-size: 12PX;
  line-height: 1;
  font-variant-numeric: tabular-nums;
}

.activity-promotion__countdown strong {
  color: #ffd166;
  font-size: 14PX;
}

.activity-promotion__name {
  flex: 0 1 auto;
  max-width: 140PX;
  overflow: hidden;
  padding: 5PX 9PX;
  border-radius: 999PX;
  color: #f4eaff;
  background: rgba(255, 255, 255, 0.1);
  box-shadow: 0 0 12PX rgba(64, 255, 215, 0.16);
  font-size: 12PX;
  line-height: 1;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.activity-promotion__title {
  flex: 0 1 auto;
  min-width: 0;
  font-size: 14PX;
  line-height: 1.4;
  white-space: nowrap;
  text-shadow: 0 0 12PX rgba(144, 255, 235, 0.34);
}

.activity-promotion__divider {
  width: 1PX;
  height: 16PX;
  flex: 0 0 auto;
  background: rgba(255, 255, 255, 0.28);
}

.activity-promotion__description {
  min-width: 0;
  overflow: hidden;
  color: rgba(255, 255, 255, 0.82);
  font-size: 13PX;
  line-height: 1.4;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.activity-promotion__action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  min-width: 104PX;
  height: 32PX;
  padding: 0 14PX;
  gap: 4PX;
  border: 1PX solid rgba(255, 209, 102, 0.72);
  border-radius: 6PX;
  color: #2a1746;
  background: #ffd166;
  font-size: 13PX;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.18s ease, box-shadow 0.18s ease;
}

.activity-promotion__action:hover,
.activity-promotion__action:focus-visible {
  outline: none;
  background: #ffe09a;
  box-shadow: 0 0 0 3PX rgba(255, 209, 102, 0.2);
}

.activity-promotion__action {
  box-shadow: 0 0 14PX rgba(255, 209, 102, 0.28);
}

.activity-promotion__close {
  position: absolute;
  top: 2PX;
  right: 12PX;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44PX;
  height: 44PX;
  padding: 0;
  border: 0;
  color: rgba(255, 255, 255, 0.74);
  background: transparent;
  font-size: 17PX;
  cursor: pointer;
}

.activity-promotion__close:hover,
.activity-promotion__close:focus-visible {
  color: #fff;
  outline: none;
}

@media (max-width: 760px) {
  .activity-promotion {
    padding: 0 48PX 0 12PX;
  }

  .activity-promotion__content {
    justify-content: flex-start;
    width: 100%;
    gap: 8PX;
  }

  .activity-promotion__badge,
  .activity-promotion__countdown,
  .activity-promotion__divider,
  .activity-promotion__description {
    display: none;
  }

  .activity-promotion__name {
    max-width: 7em;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .activity-promotion__countdown.activity-promotion__long-term {
    display: inline-flex;
  }

  .activity-promotion__title {
    flex: 1 1 auto;
    overflow: hidden;
    font-size: 13PX;
    text-overflow: ellipsis;
  }

  .activity-promotion__action {
    min-width: 92PX;
    padding: 0 10PX;
  }

  .activity-promotion__close {
    right: 2PX;
  }
}

@media (prefers-reduced-motion: reduce) {
  .activity-promotion__action {
    transition: none;
  }
}
</style>
